import { recordBufferedLogEvent } from '@/utils/logBuffer';
import { getActiveSyncTraceId } from './syncDiagnosticEvents';

export type SQLiteOperationKind = 'read' | 'write' | 'bulk' | 'close';
export type SQLiteOperationPhase = 'queued' | 'executing' | 'transaction-start' | 'transaction-body' | 'transaction-finalizing' | 'retry-wait' | 'close';
export type SQLiteErrorClass = 'sqlite_busy' | 'sqlite_locked' | 'other';
export const SQLITE_DIAGNOSTIC_LIMITS = { active: 64, recent: 32, overlaps: 8, activeAgeMs: 5 * 60_000, recentAgeMs: 15 * 60_000 } as const;

export type SQLiteOperationView = {
  operationId: string;
  adapterId: string;
  sessionId: string;
  method: string;
  table: string | null;
  kind: SQLiteOperationKind;
  phase: SQLiteOperationPhase;
  connectionId: string;
  attempts: number;
  queueWaitMs: number;
  executionMs: number;
  retryWaitMs: number;
  totalMs: number;
};
export type SQLiteOperationDiagnostic = SQLiteOperationView & {
  traceId?: string;
  outcome: 'recovered' | 'failure';
  errorClass: SQLiteErrorClass;
  errorCode: string | number | null;
  rollbackObserved: false;
  overlaps: SQLiteOperationView[];
  contentionSource: 'observed_overlap' | 'external_or_unknown';
};
type Failure = { table: string | null; errorClass: SQLiteErrorClass; errorCode: string | number | null; overlaps: SQLiteOperationView[]; phase: SQLiteOperationPhase; connectionId: string };
export type SQLiteOperationContext = {
  view: Omit<SQLiteOperationView, 'queueWaitMs' | 'executionMs' | 'retryWaitMs' | 'totalMs'>;
  queuedAt: number;
  startedAt?: number;
  retryWaitStartedAt?: number;
  retryWaitMs?: number;
  traceId?: string;
  failure?: Failure;
  finished?: boolean;
};
const clock = (): number => typeof performance !== 'undefined' && typeof performance.now === 'function' ? performance.now() : Date.now();
let sequence = 0;
const active = new Map<string, SQLiteOperationContext>();
const recent: { at: number; value: SQLiteOperationDiagnostic }[] = [];
const byError = new WeakMap<object, { at: number; value: SQLiteOperationDiagnostic }>();
const methods = new Set(['create', 'update', 'delete', 'retire', 'structural', 'pending-register', 'pending-remove', 'pending-remove-conditional', 'conditional-update', 'conditional-delete', 'recovery-snapshot', 'full-state-snapshot', 'checklist-snapshot-create', 'checklist-snapshot-reconcile', 'completeChecklistMedia', 'persistChecklistMediaPending', 'bulkUpsertWithPending', 'bulkUpsert', 'bulkInsertIfAbsent', 'readItem', 'listItems', 'getPendingOperations', 'compile_options', 'close']);
const elapsed = (now: number, then: number): number => Math.max(0, Math.round(now - then));
const retryWaitAt = (context: SQLiteOperationContext, now: number): number => (context.retryWaitMs ?? 0) + (context.retryWaitStartedAt === undefined ? 0 : elapsed(now, context.retryWaitStartedAt));
const viewAt = (context: SQLiteOperationContext, now: number): SQLiteOperationView => ({
  ...context.view,
  queueWaitMs: elapsed(context.startedAt ?? now, context.queuedAt),
  executionMs: context.startedAt === undefined ? 0 : Math.max(0, elapsed(now, context.startedAt) - retryWaitAt(context, now)),
  retryWaitMs: retryWaitAt(context, now),
  totalMs: elapsed(now, context.queuedAt),
});
const prune = (now: number): void => {
  for (const [id, context] of active) if (now - context.queuedAt > SQLITE_DIAGNOSTIC_LIMITS.activeAgeMs) active.delete(id);
  while (recent.length && now - recent[0].at > SQLITE_DIAGNOSTIC_LIMITS.recentAgeMs) recent.shift();
};
export const classifySQLiteOperationError = (error: unknown): { errorClass: SQLiteErrorClass; errorCode: string | number | null } => {
  try {
    const raw = error as { code?: unknown; message?: unknown; cause?: { code?: unknown; message?: unknown } } | null;
    const code = raw?.code ?? raw?.cause?.code;
    const errorCode = typeof code === 'number' && Number.isInteger(code) ? code
      : typeof code === 'string' && code.length <= 64 && /^SQLITE_[A-Z_]+$/.test(code) ? code : null;
    const text = [raw?.message, raw?.cause?.message, code, typeof error === 'string' ? error : ''].filter(value => typeof value === 'string').join(' ');
    if ((typeof code === 'number' && (code & 255) === 6) || /sqlite_locked|database (?:table|schema) is locked/i.test(text)) return { errorClass: 'sqlite_locked', errorCode };
    if ((typeof code === 'number' && (code & 255) === 5) || /sqlite_busy|database is locked/i.test(text)) return { errorClass: 'sqlite_busy', errorCode };
    return { errorClass: 'other', errorCode };
  } catch { return { errorClass: 'other', errorCode: null }; }
};

/** Tudo neste observador é melhor esforço: nenhuma falha altera o resultado SQLite. */
export const beginSQLiteOperation = (adapterId: string, session: number, label: string, kind: SQLiteOperationKind, table: string | null = null): SQLiteOperationContext | undefined => {
  try {
    const now = clock(); prune(now);
    if (active.size >= SQLITE_DIAGNOSTIC_LIMITS.active) active.delete(active.keys().next().value!);
    const operationId = `sqlite-op-${++sequence}`;
    const method = methods.has(label.split(':')[0]) ? label.split(':')[0] : 'unknown';
    const context: SQLiteOperationContext = {
      queuedAt: now,
      traceId: getActiveSyncTraceId(),
      view: { operationId, adapterId, sessionId: `${adapterId}:${session}`, method, table: table && /^[A-Za-z_][A-Za-z0-9_]{0,79}$/.test(table) ? table : null, kind, phase: 'queued', connectionId: `${adapterId}:${session}:main`, attempts: 0 },
    };
    active.set(operationId, context);
    return context;
  } catch { return undefined; }
};
export const startSQLiteOperation = (context?: SQLiteOperationContext): void => {
  try { if (context) { context.startedAt = clock(); context.view.phase = 'executing'; context.view.attempts = 1; } } catch { /* Melhor esforço. */ }
};
export const setSQLiteOperationPhase = (context: SQLiteOperationContext | undefined, phase: SQLiteOperationPhase, exclusive = false): void => {
  try {
    if (!context) return;
    const now = clock();
    if (context.retryWaitStartedAt !== undefined) {
      context.retryWaitMs = retryWaitAt(context, now);
      context.retryWaitStartedAt = undefined;
    }
    if (phase === 'retry-wait') context.retryWaitStartedAt = now;
    context.view.phase = phase;
    if (exclusive) context.view.connectionId = `${context.view.operationId}:exclusive:${context.view.attempts}`;
  } catch { /* Melhor esforço. */ }
};
export const setSQLiteOperationTable = (context: SQLiteOperationContext | undefined, table: string): void => {
  try { if (context && /^[A-Za-z_][A-Za-z0-9_]{0,79}$/.test(table)) context.view.table = table; } catch { /* Melhor esforço. */ }
};
export const setSQLiteOperationAttempt = (context: SQLiteOperationContext | undefined, attempt: number): void => {
  try { if (context) context.view.attempts = attempt; } catch { /* Melhor esforço. */ }
};
export const noteSQLiteOperationFailure = (context: SQLiteOperationContext | undefined, error: unknown): void => {
  try {
    if (!context) return;
    const now = clock(); prune(now);
    const overlaps = [...active.values()].filter(other => other !== context && other.startedAt !== undefined && other.view.phase !== 'retry-wait').slice(0, SQLITE_DIAGNOSTIC_LIMITS.overlaps).map(other => viewAt(other, now));
    context.failure = { table: context.view.table, ...classifySQLiteOperationError(error), overlaps, phase: context.view.phase, connectionId: context.view.connectionId };
  } catch { /* Melhor esforço. */ }
};
export const finishSQLiteOperation = (context: SQLiteOperationContext | undefined, error?: unknown, failed = false): void => {
  try {
    if (!context || context.finished) return;
    const now = clock();
    if (failed && !context.failure) noteSQLiteOperationFailure(context, error);
    if (context.failure) {
      const value: SQLiteOperationDiagnostic = {
        ...viewAt(context, now), ...context.failure,
        traceId: context.traceId, outcome: failed ? 'failure' : 'recovered', rollbackObserved: false,
        contentionSource: context.failure.overlaps.length ? 'observed_overlap' : 'external_or_unknown',
      };
      if (failed) {
        recent.push({ at: now, value });
        if (recent.length > SQLITE_DIAGNOSTIC_LIMITS.recent) recent.shift();
        if (error && (typeof error === 'object' || typeof error === 'function')) byError.set(error, { at: now, value });
      }
      try { recordBufferedLogEvent(failed ? 'warn' : 'info', 'sqlite_operation', value.outcome, { sqliteOperation: value }); } catch { /* O buffer não participa da operação. */ }
    }
  } catch { /* Melhor esforço. */ }
  finally { if (context) { context.finished = true; active.delete(context.view.operationId); } }
};
export const getSQLiteOperationFailureContext = (error?: unknown, traceId?: string): SQLiteOperationDiagnostic[] => {
  try {
    const now = clock(); prune(now);
    const exact = error && (typeof error === 'object' || typeof error === 'function') ? byError.get(error) : undefined;
    if (exact && now - exact.at <= SQLITE_DIAGNOSTIC_LIMITS.recentAgeMs) return [exact.value];
    return traceId ? recent.filter(entry => entry.value.traceId === traceId).slice(-8).map(entry => entry.value) : [];
  } catch { return []; }
};
export const getSQLiteOperationActivity = (): SQLiteOperationView[] => {
  try { const now = clock(); prune(now); return [...active.values()].map(context => viewAt(context, now)); } catch { return []; }
};
